import { ImgElement } from '@axinom/mosaic-ui';
import { config } from 'config';
import * as lscache from 'lscache';
import React from 'react';
import logo from '../../../images/AP_logo.png';
import { SSOResponseCode } from '../../../models/ssoTypes';
import { authStateStore } from '../../../state-store';
import { clearAllAuthStorage } from '../../../util/helper';
import classes from './SignInError.module.scss';

function contactAxinomSupport(error: {
  code: number | undefined;
  message: string | undefined;
}): void {
  window.location.href = `mailto:support@axinom.com?subject=Axinom Mosaic Environment Administration - Sign In Error&body=<Replace with your message> %0D%0A%0D%0A========================[Auto Generated Details]========================%0D%0A%0D%0AError Code: ${error?.code}%0D%0AError Message: ${error?.message}`;
}

const switchUser = async (): Promise<void> => {
  lscache.remove('switchedFromTenantId');

  const logoutURL = new URL(
    '/api/sso/logout',
    config.axinomPortalBaseUrl,
  ).toString();

  await fetch(logoutURL, {
    credentials: 'include',
  });

  window.location.href = new URL(
    `/login?returnUrl=${window.location.href}`,
    config.axinomPortalBaseUrl,
  ).toString();
};

const handlePortalRedirect = (): void => {
  lscache.remove('switchedFromTenantId');
  if (config.axinomPortalBaseUrl !== undefined) {
    window.location.href = new URL(config.axinomPortalBaseUrl).toString();
  }
};

export const SignInError: React.FC = () => {
  const ssoContext = authStateStore.useState('ssoContext');
  clearAllAuthStorage();

  /**
   * Error Codes:
   *  900 - No associated tenants for the user.
   *  901 - Request failed to Axinom Portal. Either client error or CORS error.
   * <900 - Http Server errors returned from Axinom Portal.
   */
  return (
    <div className={classes.container}>
      <div className={classes.wrapper}>
        <div className={classes.box}>
          <div className={classes.logo}>
            <ImgElement src={logo} alt="Administration Portal" />
          </div>
          <div className={classes.title}>Sign In Error</div>
          {ssoContext.ssoError?.code === SSOResponseCode.NO_VALID_TENANTS ? (
            <ErrorNoAssociatedTenants
              code={ssoContext.ssoError.code}
              message={ssoContext.ssoError.message}
            />
          ) : ssoContext.ssoError?.code === SSOResponseCode.REQUEST_FAILED ? (
            <ErrorRequestFailed
              code={ssoContext.ssoError.code}
              message={ssoContext.ssoError.message}
            />
          ) : (
            // Axinom Portal originated related error codes will be rendered in `ErrorGeneric`
            <ErrorGeneric
              code={ssoContext.ssoError?.code}
              message={ssoContext.ssoError?.message}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ErrorNoAssociatedTenants: React.FC<{
  code: number | undefined;
  message: string | undefined;
}> = ({ code, message }) => {
  return (
    <div className={classes.errorBox}>
      <div className={classes.subText}>
        Seems like you don&apos;t have a Mosaic Tenant associated with the email{' '}
        <strong>{message}</strong>.
      </div>
      <div className={classes.subText}>
        You can subscribe to a free{' '}
        <a
          href={`${
            new URL('mosaic/free-trial', config.axinomPortalBaseUrl).href
          }`}
        >
          Mosaic trial
        </a>{' '}
        and check back here. If you still think something is not right, please
        contact Axinom Support.
      </div>
      <ContactAxinomSupport
        code={code}
        message={`No Mosaic tenant associated for email ${message}`}
        displayButton={true}
      />
      <SignOut />
      <SwitchUser displayButton={false} />
      <GoToAxinomPortal displayButton={false} />
    </div>
  );
};

const ErrorRequestFailed: React.FC<{
  code: number | undefined;
  message: string | undefined;
}> = ({ code, message }) => {
  return (
    <div className={classes.errorBox}>
      <div className={classes.subText}>{message}</div>
      <ContactAxinomSupport
        code={code}
        message={`Error connecting to the Axinom Portal. Could be either caused by a client network error or CORS policy misconfiguration.`}
        displayButton={false}
      />
    </div>
  );
};

const ErrorGeneric: React.FC<{
  code: number | undefined;
  message: string | undefined;
}> = ({ message }) => {
  return (
    <div className={classes.errorBox}>
      <div className={classes.subText}>{message}</div>
      <GoToAxinomPortal displayButton={true} />
      <SignOut />
      <SwitchUser displayButton={true} />
    </div>
  );
};

const ContactAxinomSupport: React.FC<{
  code: number | undefined;
  message: string | undefined;
  displayButton: boolean;
}> = ({ code, message, displayButton }) => {
  const displayText = 'Contact Axinom Support';
  return displayButton ? (
    <div
      className={classes.button}
      onClick={() => contactAxinomSupport({ code, message })}
    >
      {displayText}
    </div>
  ) : (
    <div
      className={classes.linkButton}
      onClick={() => contactAxinomSupport({ code, message })}
    >
      <a>
        <strong>{displayText}</strong>
      </a>
    </div>
  );
};

const SwitchUser: React.FC<{
  displayButton: boolean;
}> = ({ displayButton }) => {
  const displayText = 'Switch User';
  return displayButton ? (
    <div className={classes.button} onClick={switchUser}>
      {displayText}
    </div>
  ) : (
    <div className={classes.linkButton} onClick={switchUser}>
      <a>
        <strong>{displayText}</strong>
      </a>
    </div>
  );
};

const GoToAxinomPortal: React.FC<{
  displayButton: boolean;
}> = ({ displayButton }) => {
  const displayText = 'Go to Axinom Portal';
  return displayButton ? (
    <div className={classes.button} onClick={handlePortalRedirect}>
      {displayText}
    </div>
  ) : (
    <div className={classes.linkButton} onClick={handlePortalRedirect}>
      <a>
        <strong>{displayText}</strong>
      </a>
    </div>
  );
};

const SignOut: React.FC = () => {
  const handleSignOut = async (): Promise<void> => {
    if (config.axinomPortalBaseUrl !== undefined) {
      clearAllAuthStorage();

      //redirect to the Axinom Portal for sign out.
      window.location.href = new URL(
        '/logout',
        config.axinomPortalBaseUrl,
      ).toString();
    }
  };
  return (
    <div className={classes.button} onClick={handleSignOut}>
      Sign Out
    </div>
  );
};
